import React from 'react';

interface NotionTableProps {
  children: React.ReactNode;
  className?: string;
}

interface NotionTableHeaderProps {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}

interface NotionTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface NotionTableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
}

interface NotionTableCellProps {
  children: React.ReactNode;
  className?: string;
  header?: boolean;
}

export function NotionTable({ children, className = '' }: NotionTableProps) {
  return (
    <div
      className={`
        bg-white dark:bg-gray-800
        border border-[rgba(55,53,47,0.16)] dark:border-gray-700
        rounded-lg overflow-hidden
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export function NotionTableHeader({ children, className = '', sticky = true }: NotionTableHeaderProps) {
  return (
    <div
      className={`
        ${sticky ? 'sticky top-0 z-10' : ''}
        bg-[#F7F7F5] dark:bg-gray-900
        border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export function NotionTableBody({ children, className = '' }: NotionTableBodyProps) {
  return (
    <div
      className={`
        divide-y divide-[rgba(55,53,47,0.09)] dark:divide-gray-700
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export function NotionTableRow({ children, className = '', onClick, selected = false }: NotionTableRowProps) {
  return (
    <div
      onClick={onClick}
      className={`
        hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/30
        transition-colors
        ${onClick ? 'cursor-pointer' : ''}
        ${selected ? 'bg-[rgba(35,131,226,0.1)] dark:bg-blue-900/20' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export function NotionTableCell({ children, className = '', header = false }: NotionTableCellProps) {
  if (header) {
    return (
      <div
        className={`
          text-xs font-medium uppercase tracking-wider
          text-[#787774] dark:text-gray-400
          ${className}
        `}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={`
        text-sm text-[#37352F] dark:text-gray-200
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// Compound component pattern
NotionTable.Header = NotionTableHeader;
NotionTable.Body = NotionTableBody;
NotionTable.Row = NotionTableRow;
NotionTable.Cell = NotionTableCell;

export default NotionTable;
